import React, { useState, useMemo } from 'react';
import { format, parseISO } from 'date-fns';
import { AlertTriangle, CalendarDays, Check, Loader2, EyeOff } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (amount = 0) =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(amount);

const todayISO = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
};

export const OverdueWarningCard = ({ overdue, onResolved }) => {
  const [open, setOpen] = useState(false);
  const totalCount = (overdue?.invoices?.length || 0) + (overdue?.bills?.length || 0);

  if (!overdue || totalCount === 0) return null;

  return (
    <>
      <Card
        className="border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/10 dark:border-amber-900/40 shadow-sm"
        data-testid="overdue-warning-card"
      >
        <CardContent className="py-4">
          <div className="flex items-start gap-4">
            <div className="p-2.5 rounded-lg bg-amber-100 dark:bg-amber-900/30 shrink-0">
              <AlertTriangle className="w-5 h-5 text-amber-700 dark:text-amber-300" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-200" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {totalCount} overdue item{totalCount === 1 ? '' : 's'} missing from your forecast
              </h3>
              <p className="text-[12px] text-amber-800/80 dark:text-amber-300/80 mt-0.5 leading-relaxed">
                {overdue.invoices.length > 0 && (
                  <span><span className="font-semibold">{formatCurrency(overdue.total_overdue_ar)}</span> in invoices · </span>
                )}
                {overdue.bills.length > 0 && (
                  <span><span className="font-semibold">{formatCurrency(overdue.total_overdue_ap)}</span> in bills · </span>
                )}
                payment dates before today. Reschedule them to keep the cash flow forecast accurate.
              </p>
            </div>
            <Button
              size="sm"
              onClick={() => setOpen(true)}
              className="gap-1.5 bg-amber-600 hover:bg-amber-700 text-white shadow-sm shrink-0"
              data-testid="overdue-review-btn"
            >
              <CalendarDays className="w-3.5 h-3.5" />
              Review & reschedule
            </Button>
          </div>
        </CardContent>
      </Card>
      {open && (
        <OverdueDialog
          overdue={overdue}
          onClose={() => setOpen(false)}
          onResolved={() => {
            setOpen(false);
            onResolved && onResolved();
          }}
        />
      )}
    </>
  );
};

const OverdueDialog = ({ overdue, onClose, onResolved }) => {
  // Per-row state: { [id]: { mode, extraDays, newDate, saving, done, selected } }
  const initial = useMemo(() => {
    const map = {};
    [...overdue.invoices, ...overdue.bills].forEach((it) => {
      map[it.id] = { mode: 'date', extraDays: it.extra_days || 0, newDate: '', saving: false, done: false, selected: false };
    });
    return map;
  }, [overdue]);
  const [rows, setRows] = useState(initial);
  const [dismissing, setDismissing] = useState(false);

  const update = (id, patch) => setRows((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const allItems = [...overdue.invoices, ...overdue.bills];

  const handleSaveRow = async (it) => {
    const row = rows[it.id];
    if (!row) return;
    update(it.id, { saving: true });
    try {
      const endpoint = it.type === 'invoice'
        ? `${API}/invoices/${it.id}/reschedule`
        : `${API}/bills/${it.id}/reschedule`;
      let newDate = row.newDate;
      if (row.mode === 'days') {
        const d = new Date();
        d.setDate(d.getDate() + (parseInt(row.extraDays, 10) || 0));
        newDate = format(d, 'yyyy-MM-dd');
      }
      if (!newDate) {
        toast.error('Pick a new date or enter days from today');
        update(it.id, { saving: false });
        return;
      }
      await axios.post(endpoint, { new_payment_date: newDate });
      update(it.id, { saving: false, done: true });
      toast.success(`${it.type === 'invoice' ? 'Invoice' : 'Bill'} rescheduled to ${format(parseISO(newDate), 'dd MMM yyyy')}`);
    } catch (e) {
      update(it.id, { saving: false });
      toast.error(e?.response?.data?.detail || 'Failed to reschedule');
    }
  };

  const dismiss = async (ids) => {
    const invoice_ids = ids.filter((id) => overdue.invoices.some((i) => i.id === id));
    const bill_ids = ids.filter((id) => overdue.bills.some((b) => b.id === id));
    if (invoice_ids.length === 0 && bill_ids.length === 0) {
      toast.error('No items selected');
      return;
    }
    setDismissing(true);
    try {
      const r = await axios.post(`${API}/overdue/dismiss`, { invoice_ids, bill_ids });
      const total = (r.data?.invoices_dismissed || 0) + (r.data?.bills_dismissed || 0);
      toast.success(`${total} item${total === 1 ? '' : 's'} dismissed. They won't reappear unless their amount or date changes.`);
      onResolved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to dismiss');
    } finally {
      setDismissing(false);
    }
  };

  const handleDismissSelected = () => {
    const selectedIds = allItems.filter((it) => rows[it.id]?.selected && !rows[it.id]?.done).map((it) => it.id);
    dismiss(selectedIds);
  };

  const handleDismissAll = () => {
    const allIds = allItems.filter((it) => !rows[it.id]?.done).map((it) => it.id);
    dismiss(allIds);
  };

  const remainingCount = allItems.filter((it) => !rows[it.id]?.done).length;
  const selectedCount = allItems.filter((it) => rows[it.id]?.selected && !rows[it.id]?.done).length;
  const anyDone = remainingCount < allItems.length;
  const allSelectableSelected = remainingCount > 0 && selectedCount === remainingCount;

  const toggleSelectAll = () => {
    const next = !allSelectableSelected;
    setRows((prev) => {
      const out = { ...prev };
      allItems.forEach((it) => {
        if (!out[it.id]?.done) out[it.id] = { ...out[it.id], selected: next };
      });
      return out;
    });
  };

  return (
    <Dialog open onOpenChange={(v) => !v && (anyDone ? onResolved() : onClose())}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="overdue-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            Reschedule overdue items
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500 mt-1">
            Pick a new expected payment date for each item, or dismiss items you don't want to see again.
            Dismissed items only reappear if their amount or date changes (e.g. a Xero sync edits them).
          </DialogDescription>
        </DialogHeader>

        {/* Select-all toolbar */}
        {remainingCount > 0 && (
          <div className="flex items-center justify-between border-y border-slate-100 dark:border-slate-800 px-1 py-2 -mx-6 px-7 bg-slate-50/60 dark:bg-slate-900/40">
            <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300 cursor-pointer select-none">
              <Checkbox
                checked={allSelectableSelected}
                onCheckedChange={toggleSelectAll}
                data-testid="overdue-select-all"
              />
              {allSelectableSelected ? 'Deselect all' : 'Select all'}
              {selectedCount > 0 && (
                <span className="text-primary font-semibold">· {selectedCount} selected</span>
              )}
            </label>
          </div>
        )}

        <div className="flex-1 overflow-auto -mx-6 px-6 py-2 space-y-3">
          {allItems.map((it) => (
            <OverdueRow
              key={it.id}
              item={it}
              row={rows[it.id]}
              onChange={(patch) => update(it.id, patch)}
              onSave={() => handleSaveRow(it)}
            />
          ))}
        </div>

        <DialogFooter className="border-t border-slate-100 pt-3 flex items-center justify-between sm:justify-between gap-2 flex-wrap">
          <span className="text-xs text-slate-500">
            {allItems.length - remainingCount} of {allItems.length} rescheduled
          </span>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={handleDismissSelected}
              disabled={dismissing || selectedCount === 0}
              className="gap-1.5"
              data-testid="overdue-dismiss-selected-btn"
            >
              {dismissing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <EyeOff className="w-3.5 h-3.5" />}
              Dismiss selected {selectedCount > 0 ? `(${selectedCount})` : ''}
            </Button>
            <Button
              variant="outline"
              onClick={handleDismissAll}
              disabled={dismissing || remainingCount === 0}
              className="gap-1.5 border-amber-300 text-amber-700 hover:bg-amber-50"
              data-testid="overdue-dismiss-all-btn"
            >
              {dismissing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <EyeOff className="w-3.5 h-3.5" />}
              Dismiss all
            </Button>
            <Button variant="outline" onClick={onClose} data-testid="overdue-dialog-close">
              Close
            </Button>
            <Button
              onClick={onResolved}
              className="gap-1.5"
              data-testid="overdue-dialog-done"
            >
              <Check className="w-4 h-4" />
              Done · Refresh
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const OverdueRow = ({ item, row, onChange, onSave }) => {
  if (!row) return null;
  const minDate = todayISO();
  return (
    <div
      className={`rounded-lg border p-3 transition-colors ${row.done ? 'border-emerald-300 bg-emerald-50/60 dark:bg-emerald-900/15' : row.selected ? 'border-primary/60 bg-primary/5' : 'border-slate-200 dark:border-slate-700'}`}
      data-testid={`overdue-row-${item.id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
        <div className="flex items-start gap-2 min-w-0 flex-1">
          {!row.done && (
            <Checkbox
              checked={!!row.selected}
              onCheckedChange={(v) => onChange({ selected: !!v })}
              className="mt-0.5"
              data-testid={`overdue-row-select-${item.id}`}
            />
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${item.type === 'invoice' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300'}`}>
                {item.type === 'invoice' ? 'Invoice (AR)' : 'Bill (AP)'}
              </span>
              <span className="font-medium text-sm text-slate-900 dark:text-slate-100 truncate">
                {item.counterparty_name}
              </span>
              {item.document_number && (
                <span className="text-xs text-slate-500 font-mono">· {item.document_number}</span>
              )}
            </div>
            <p className="text-[11px] text-slate-500 mt-1">
              Issued {format(parseISO(item.issue_date), 'dd MMM yyyy')}
              {' · '}was due <span className="text-amber-700 font-medium">{format(parseISO(item.expected_payment_date), 'dd MMM yyyy')}</span>
              {' · '}<span className="text-rose-600 font-semibold">{item.days_overdue}d overdue</span>
            </p>
          </div>
        </div>
        <span className="font-mono font-semibold text-slate-900 dark:text-slate-100 text-base">
          {formatCurrency(item.amount)}
        </span>
      </div>

      {row.done ? (
        <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300 text-xs font-medium">
          <Check className="w-4 h-4" />
          Rescheduled. Refresh the dashboard to see updated forecast.
        </div>
      ) : (
        <div className="flex items-end gap-2 flex-wrap" data-testid={`overdue-row-form-${item.id}`}>
          <div className="flex items-center gap-1.5 rounded-md border border-slate-200 dark:border-slate-700 p-0.5 bg-white dark:bg-slate-900">
            <button
              type="button"
              onClick={() => onChange({ mode: 'date' })}
              className={`px-2.5 py-1 text-[11px] font-medium rounded transition-colors ${row.mode === 'date' ? 'bg-primary text-white' : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'}`}
              data-testid={`overdue-mode-date-${item.id}`}
            >
              Pick date
            </button>
            <button
              type="button"
              onClick={() => onChange({ mode: 'days' })}
              className={`px-2.5 py-1 text-[11px] font-medium rounded transition-colors ${row.mode === 'days' ? 'bg-primary text-white' : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'}`}
              data-testid={`overdue-mode-days-${item.id}`}
            >
              Days from today
            </button>
          </div>

          {row.mode === 'date' ? (
            <div className="space-y-0.5">
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">New due date</Label>
              <Input
                type="date"
                min={minDate}
                value={row.newDate}
                onChange={(e) => onChange({ newDate: e.target.value })}
                className="h-9 w-44 font-mono"
                data-testid={`overdue-date-input-${item.id}`}
              />
            </div>
          ) : (
            <div className="space-y-0.5">
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Days from today</Label>
              <Input
                type="number"
                min="0"
                value={row.extraDays}
                onChange={(e) => onChange({ extraDays: e.target.value })}
                placeholder="e.g. 14"
                className="h-9 w-32 font-mono"
                data-testid={`overdue-days-input-${item.id}`}
              />
            </div>
          )}

          <Button
            size="sm"
            onClick={onSave}
            disabled={row.saving}
            className="gap-1.5 h-9"
            data-testid={`overdue-save-${item.id}`}
          >
            {row.saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            Save
          </Button>
        </div>
      )}
    </div>
  );
};

export default OverdueWarningCard;
